import { apiClient, ApiError } from './apiClient';
import { parseEntryIds } from './timesheetParsers';
import { format } from 'date-fns';
import type { Timesheet, TimesheetEntry, Project, CostCentre, Employee, BatchEntryRequest, BatchEntryResponse } from '@/types/timesheet';

/**
 * Pagination response metadata from the API
 */
export interface PaginationInfo {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Paginated response for projects
 */
export interface PaginatedProjectsResponse {
  data: Project[];
  pagination: PaginationInfo;
}

/**
 * Parameters for fetching paginated projects
 */
export interface GetProjectsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  companyId?: string;
}

/**
 * Prepare a single entry for the backend API.
 * Converts frontend string IDs (e.g. overtimeTypeId) to the numeric types
 * the C# model expects, preventing System.Text.Json deserialization failures.
 */
function prepareEntryForApi({ id, ...entry }: TimesheetEntry): Record<string, unknown> {
  return {
    ...entry,
    projectId: entry.projectId ? Number(entry.projectId) : null,
    costCentreId: entry.costCentreId ? Number(entry.costCentreId) : null,
    overtimeTypeId: entry.overtimeTypeId ? Number(entry.overtimeTypeId) : undefined,
    sourceIntegration: entry.sourceIntegration ?? undefined,
    absenceType: entry.absenceType ?? undefined,
    absenceId: entry.absenceId ?? undefined,
    isReadOnly: entry.isReadOnly ?? false,
  };
}

function parseTimesheetDates(timesheet: any): Timesheet {
  return {
    ...timesheet,
    weekEnding: timesheet.weekEnding ? new Date(timesheet.weekEnding) : timesheet.weekEnding,
    submittedAt: timesheet.submittedAt ? new Date(timesheet.submittedAt) : timesheet.submittedAt,
    approvedAt: timesheet.approvedAt ? new Date(timesheet.approvedAt) : timesheet.approvedAt,
    rejectedAt: timesheet.rejectedAt ? new Date(timesheet.rejectedAt) : timesheet.rejectedAt,
    recalledAt: timesheet.recalledAt ? new Date(timesheet.recalledAt) : timesheet.recalledAt,
    entries: Array.isArray(timesheet.entries)
      ? timesheet.entries.map(parseEntryIds)
      : timesheet.entries,
    // The backend serialises WorkflowInstanceId as a numeric `long`, but the
    // frontend treats workflowInstanceId as a string. Normalise it here (as with
    // projectId/costCentreId in parseEntryIds) so the string contract holds.
    workflowInstanceId:
      timesheet.workflowInstanceId != null
        ? String(timesheet.workflowInstanceId)
        : timesheet.workflowInstanceId,
  };
}

function parseProjectDates(project: any): Project {
  return {
    ...project,
    id: String(project.id),
    lastUsed: project.lastUsed ? new Date(project.lastUsed) : project.lastUsed,
    projectManager: project.projectManager ?? undefined,
    sortKeys: project.sort
      ? project.sort.split(',').map((t: string) => t.trim()).filter(Boolean)
      : undefined,
  };
}

function parseCostCentreDates(costCentre: any): CostCentre {
  return {
    ...costCentre,
    id: String(costCentre.id),
    lastUsed: costCentre.lastUsed ? new Date(costCentre.lastUsed) : costCentre.lastUsed,
  };
}

export const timesheetApi = {
  async getTimesheetById(id: number): Promise<Timesheet> {
    const timesheet = await apiClient.get<any>(`/timesheets/${id}`);
    return parseTimesheetDates(timesheet);
  },

  async getTimesheetsByEmployeeId(employeeId: number, companyId?: string): Promise<Timesheet[]> {
    const params = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
    const timesheets = await apiClient.get<any[]>(`/timesheets/employee/${employeeId}${params}`);
    return timesheets.map(parseTimesheetDates);
  },

  async getTimesheetsByEmployeeIds(employeeIds: number[], companyId?: string): Promise<Timesheet[]> {
    if (employeeIds.length === 0) {
      return [];
    }
    const queryString = employeeIds.map(id => `employeeIds=${encodeURIComponent(id)}`).join('&');
    const companyParam = companyId ? `&companyId=${encodeURIComponent(companyId)}` : '';
    const timesheets = await apiClient.get<any[]>(`/timesheets/employees?${queryString}${companyParam}`);
    return timesheets.map(parseTimesheetDates);
  },

  async createTimesheet(timesheet: Timesheet): Promise<Timesheet> {
    // Strip out entry IDs before sending - they are auto-generated by the database.
    // Send weekEnding as a date-only string (yyyy-MM-dd) to avoid timezone-induced
    // date drift caused by Date.toISOString() converting local midnight to UTC.
    const timesheetToSend = {
      ...timesheet,
      weekEnding: timesheet.weekEnding instanceof Date
        ? format(timesheet.weekEnding, 'yyyy-MM-dd')
        : timesheet.weekEnding,
      entries: timesheet.entries.map(prepareEntryForApi),
    };
    const created = await apiClient.post<any, typeof timesheetToSend>('/timesheets', timesheetToSend);
    return parseTimesheetDates(created);
  },

  async updateTimesheet(id: number, timesheet: Timesheet): Promise<void> {
    // Strip out entry IDs before sending - they are auto-generated by the database.
    // Send weekEnding as a date-only string (yyyy-MM-dd) to avoid timezone-induced
    // date drift caused by Date.toISOString() converting local midnight to UTC.
    const timesheetToSend = {
      ...timesheet,
      weekEnding: timesheet.weekEnding instanceof Date
        ? format(timesheet.weekEnding, 'yyyy-MM-dd')
        : timesheet.weekEnding,
      entries: timesheet.entries.map(prepareEntryForApi),
    };
    return apiClient.put<void, typeof timesheetToSend>(`/timesheets/${id}`, timesheetToSend);
  },

  async deleteTimesheet(id: number): Promise<void> {
    return apiClient.delete<void>(`/timesheets/${id}`);
  },

  async addTimesheetEntry(timesheetId: number, entry: TimesheetEntry): Promise<TimesheetEntry> {
    // Strip out entry ID and convert types for the backend
    const prepared = prepareEntryForApi(entry);
    return apiClient.post<TimesheetEntry, typeof prepared>(`/timesheets/${timesheetId}/entries`, prepared);
  },

  /**
   * Batch update entries for a timesheet.
   * More efficient than updating the entire timesheet when only entries change.
   */
  async batchUpdateEntries(timesheetId: number, request: BatchEntryRequest): Promise<BatchEntryResponse> {
    // Strip out entry IDs from toCreate entries - they are auto-generated by the database
    const requestToSend = {
      ...request,
      toCreate: request.toCreate.map(prepareEntryForApi),
    };
    return apiClient.post<BatchEntryResponse, typeof requestToSend>(
      `/timesheets/${timesheetId}/entries/batch`,
      requestToSend
    );
  },

  /**
   * Fetch projects with pagination support.
   * @param params - Optional pagination and filter parameters
   * @returns Paginated response with projects and pagination metadata
   */
  async getProjects(params?: GetProjectsParams): Promise<PaginatedProjectsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.companyId) queryParams.append('companyId', params.companyId);
    
    const queryString = queryParams.toString();
    const url = queryString ? `/projects?${queryString}` : '/projects';
    const response = await apiClient.get<{ data: any[]; pagination?: PaginationInfo }>(url);
    
    const projects = (response.data || []).map(parseProjectDates);
    const pagination = response.pagination || {
      page: params?.page || 1,
      pageSize: params?.pageSize || projects.length,
      totalItems: projects.length,
      totalPages: 1,
      hasNextPage: false,
      hasPreviousPage: false,
    };
    
    return { data: projects, pagination };
  },

  /**
   * Fetch all projects by paginating through every page.
   * Used by components that need the full project list.
   */
  async getAllProjects(companyId?: string): Promise<Project[]> {
    const allProjects: Project[] = [];
    let page = 1;
    const pageSize = 100;
    let hasNextPage = true;

    while (hasNextPage) {
      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('pageSize', pageSize.toString());
      if (companyId) queryParams.append('companyId', companyId);

      const response = await apiClient.get<{ data: any[]; pagination?: PaginationInfo }>(
        `/projects?${queryParams.toString()}`
      );
      const projects = (response.data || []).map(parseProjectDates);
      allProjects.push(...projects);

      hasNextPage = response.pagination?.hasNextPage ?? false;
      page++;
    }

    return allProjects;
  },

  /**
   * Fetch a single project by ID.
   * Used to load project details for existing timesheet entries.
   * @param id - The project ID
   * @returns The project or null if not found
   */
  async getProjectById(id: string): Promise<Project | null> {
    try {
      const response = await apiClient.get<any>(`/projects/${id}`);
      return parseProjectDates(response);
    } catch (error) {
      // 404 Not Found is expected when projects are deleted but still referenced in old timesheets
      if (error instanceof ApiError && error.status === 404) {
        return null;
      }
      console.error(`Failed to fetch project ${id}:`, error);
      return null;
    }
  },

  async getProjectsByIds(ids: string[]): Promise<Project[]> {
    const response = await apiClient.post<any[], string[]>('/projects/batch', ids.map(String));
    return response.map(parseProjectDates);
  },


  async getAllCostCentres(companyId?: string): Promise<CostCentre[]> {
    const params = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
    const costCentres = await apiClient.get<any[]>(`/costcentres${params}`);
    return costCentres.map(parseCostCentreDates);
  },

  async getCostCentresByProjectCode(projectCode: string): Promise<CostCentre[]> {
    const costCentres = await apiClient.get<any[]>(`/costcentres?projectCode=${encodeURIComponent(projectCode)}`);
    return costCentres.map(parseCostCentreDates);
  },

  /**
   * Fetch a single cost centre by ID.
   * Used to load cost centre details for existing timesheet entries.
   * @param id - The cost centre ID
   * @returns The cost centre or null if not found
   */
  async getCostCentreById(id: string): Promise<CostCentre | null> {
    try {
      const response = await apiClient.get<any>(`/costcentres/${id}`);
      return parseCostCentreDates(response);
    } catch (error) {
      // 404 Not Found is expected when cost centres are deleted but still referenced in old timesheets
      if (error instanceof ApiError && error.status === 404) {
        return null;
      }
      console.error(`Failed to fetch cost centre ${id}:`, error);
      return null;
    }
  },

  async getEmployeeById(id: string): Promise<Employee> {
    return apiClient.get<Employee>(`/employees/${id}`);
  },

  async getAllEmployees(): Promise<Employee[]> {
    return apiClient.get<Employee[]>('/employees');
  },
};
